import React, { useEffect, useState } from 'react';
import { CircleNotch } from '@phosphor-icons/react';
import useAnnouncer from '@/hooks/useAnnouncer';
import { DynamicSectionRenderer } from '../DynamicSectionRenderer';
import { INTENT_TO_BUTTON, IDEA_QUEUE_LABELS } from '@/config/task-display-config';
import IdeaCardRejectMenu from '@/components/shared/IdeaCardRejectMenu';
import { getCommentRejectReasons } from '@/lib/api';

/**
 * GenericTaskDetail - Fallback Renderer for Unknown Task Types
 * Authority: Law 65.1 + Law 66.1
 *
 * Contract-driven cards (e.g. ceo_approval) carry their own render config at
 * item.payload.ui_render_config.sections. When present it drives the section
 * renderer AND an Approve/Decline action bar is shown so the card is actionable
 * inline. WI-COST-APPROVAL-CARD-DETAIL-FIX-001. Plain/unknown items fall back to
 * GENERIC_CONFIG and render no action bar (unchanged behaviour).
 */

const GENERIC_CONFIG = {
    sections: [
        { field: "description", label: null, render_type: "text", required: false },
        { field: "summary", label: null, render_type: "text", required: false },
        { field: "status", label: "Status", render_type: "badge", required: false },
        { field: "priority", label: "Priority", render_type: "badge", required: false }
    ]
};

// A rejected COMMENT card offers the four owned reasons through the SAME menu the idea cards use (IdeaCardRejectMenu), fetched once.
// A failed fetch leaves the list empty and the plain Decline button below in place: a rejection is never blocked (the legacy
// `declined` value stays accepted by the route). The id prefix is the one the comment pipeline stamps on its card.
let commentRejectCodesPromise = null;
function fetchCommentRejectCodesOnce() {
    if (!commentRejectCodesPromise) {
        commentRejectCodesPromise = Promise.resolve()
            .then(() => getCommentRejectReasons())
            .catch((err) => { commentRejectCodesPromise = null; throw err; });
    }
    return commentRejectCodesPromise;
}
// Test seam only (as IdeaCardRejectMenu has): resets the memo so a suite can assert the fetch per case.
export function __resetCommentRejectCodesForTests() { commentRejectCodesPromise = null; }
const isCommentCard = (item) => String(item?.id || '').startsWith('linkedin-comment-');

export function GenericTaskDetail({ item, uiConfig, onAccept, onReject }) {
    const [commentCodes, setCommentCodes] = useState([]);
    const commentCard = isCommentCard(item);
    useEffect(() => {
        if (!commentCard) return undefined;
        let cancelled = false;
        fetchCommentRejectCodesOnce()
            .then((data) => { if (!cancelled) setCommentCodes(data?.codes || []); })
            .catch(() => { /* empty: the plain Decline button stays */ });
        return () => { cancelled = true; };
    }, [commentCard]);

    // Per-card render config (forwarded from the backend TaskContract) takes
    // precedence over the generic fallback.
    const contractConfig = item?.payload?.ui_render_config;
    const hasContractConfig =
        contractConfig && Array.isArray(contractConfig.sections) && contractConfig.sections.length > 0;
    const renderConfig = uiConfig || (hasContractConfig ? contractConfig : GENERIC_CONFIG);

    // Action bar only for contract-driven actionable cards (ceo_approval today),
    // and only when the cockpit passed working handlers. mirrors EmailTaskDetail
    // signatures: onAccept(id, work_type, payload) / onReject(id, reason, work_type).
    const showActions =
        hasContractConfig && typeof onAccept === 'function' && typeof onReject === 'function';

    const [busyAction, setBusyAction] = useState(null); // 'approve' | 'reject' | null

    // WI-GATEENVELOPE-SILENT-200-AUDIT-AND-FIX-001 (L-055) — see EmailTaskDetail
    // for the full note; these ceo_approval cards share the same endpoints.
    const [actionStatus, announceAction] = useAnnouncer();

    const handleApprove = async () => {
        if (!onAccept || busyAction) return;
        setBusyAction('approve');
        try {
            await onAccept(item.id, item.work_type, {});
        } catch (err) {
            announceAction(err?.message || 'Could not approve this item — please try again.');
        } finally {
            setBusyAction(null);
        }
    };

    const handleDecline = async (reasonId) => {
        if (!onReject || busyAction) return;
        setBusyAction('reject');
        try {
            await onReject(item.id, typeof reasonId === 'string' ? reasonId : 'declined', item.work_type);
        } catch (err) {
            announceAction(err?.message || 'Could not decline this item — please try again.');
        } finally {
            setBusyAction(null);
        }
    };

    return (
        <div className="space-y-6">
            <div role="status" aria-live="polite" className="sr-only">{actionStatus}</div>
            {showActions && (
                <div
                    data-testid="ceo-approval-action-bar"
                    className="flex items-center py-3 px-4 rounded-lg border border-slate-200 bg-white"
                >
                    {commentCard && commentCodes.length > 0 ? (
                        <IdeaCardRejectMenu
                            codes={commentCodes}
                            heading={IDEA_QUEUE_LABELS.commentRejectMenuHeading}
                            disabled={!!busyAction || !onReject}
                            onPick={(reasonId) => handleDecline(reasonId)}
                        />
                    ) : (
                        <button
                            type="button"
                            data-testid="ceo-approval-decline-btn"
                            onClick={() => handleDecline()}
                            disabled={!!busyAction || !onReject}
                            className={`min-h-[44px] px-5 py-2.5 rounded-md font-semibold text-sm transition-colors border ${INTENT_TO_BUTTON.negative.bg} ${INTENT_TO_BUTTON.negative.text} ${INTENT_TO_BUTTON.negative.border} ${INTENT_TO_BUTTON.negative.hover} disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2`}
                            style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
                        >
                            {busyAction === 'reject' && (
                                <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                            )}
                            {busyAction === 'reject' ? 'Declining...' : 'Decline'}
                        </button>
                    )}

                    <div className="flex-1" />

                    <button
                        type="button"
                        data-testid="ceo-approval-approve-btn"
                        onClick={handleApprove}
                        disabled={!!busyAction || !onAccept}
                        className={`min-h-[44px] px-5 py-2.5 rounded-md font-semibold text-sm transition-colors ${INTENT_TO_BUTTON.primary.bg} ${INTENT_TO_BUTTON.primary.text} ${INTENT_TO_BUTTON.primary.hover} disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2`}
                        style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
                    >
                        {busyAction === 'approve' && (
                            <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                        )}
                        {busyAction === 'approve' ? 'Approving...' : 'Approve'}
                    </button>
                </div>
            )}

            <DynamicSectionRenderer sections={renderConfig.sections} item={item} />
        </div>
    );
}
